// frontend/src/components/Mobile/FastReact/MobileRestockGroups.jsx
//
// Mobile version of "Reabastecer (Agrupado)" (RestockGroups.jsx).
//   - lists the RESTOCK preparation lines grouped by product + variant + lot
//     (across every order), oldest SLA timer first
//   - tap a group to expand its lines; each line has Iniciar (advance → COMPLETED)
//     and Desfazer (revert), both behind a confirmation to avoid mis-taps
//   - same endpoints as the desktop screen (getRestockGroups / getRestockGroupItems /
//     advancePreparationStatus / revertPreparationStatus)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fastReactAPI } from '../../../api/client';
import { FiRefreshCw, FiArrowLeft, FiChevronRight, FiPlay, FiRotateCcw, FiFilter, FiX, FiClock } from 'react-icons/fi';
import MobileLayout from '../MobileLayout';
import Toast from '../../Common/Toast';
import { useToast } from '../../../hooks/useToast';
import '../Mobile.css';

// Same static SLA timer as the desktop screen: computed on each render/refresh,
// not on an interval.
const RESTOCK_SLA_MINUTES = 5 * 60; // 5h00
const formatRestockTimer = (restockRequestAt) => {
  if (!restockRequestAt) return null;
  const elapsedMs = Date.now() - new Date(restockRequestAt).getTime();
  const remainingMin = RESTOCK_SLA_MINUTES - Math.floor(elapsedMs / 60000);
  const overdue = remainingMin < 0;
  const abs = Math.abs(remainingMin);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return { text: `${overdue ? '-' : ''}${hh}:${mm}`, overdue };
};

const fmtQty = (v) =>
  v != null
    ? parseFloat(v).toLocaleString('pt-PT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : '—';

const groupKey = (g) => `${g.product_code}::${g.variant_code ?? ''}::${g.lot_no ?? ''}`;

const FILTER_FIELDS = [
  ['po_code',      'Ordem'],
  ['product_code', 'Produto'],
  ['variant_code', 'Variante'],
  ['lot_no',       'Lote'],
];

const MobileRestockGroups = () => {
  const { toasts, removeToast, toast_success, toast_error } = useToast();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [expandedKey, setExpandedKey] = useState(null);
  const [itemsByGroup, setItemsByGroup] = useState({});
  const [loadingItems, setLoadingItems] = useState(false);

  // ── Filters (debounced free text, same pattern as the desktop screen) ──
  const [filters, setFilters] = useState({ po_code: '', product_code: '', variant_code: '', lot_no: '' });
  const [inputs, setInputs] = useState({ po_code: '', product_code: '', variant_code: '', lot_no: '' });
  const [showFilters, setShowFilters] = useState(false);
  const debounces = useRef({});
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // confirmation dialog: { type: 'advance' | 'revert', group, item }
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadGroups = useCallback(async (f) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await fastReactAPI.getRestockGroups(params);
      if (r.data.success) setGroups(r.data.groups);
      else setError(r.data.error || 'Erro ao carregar reabastecimentos.');
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao carregar reabastecimentos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadGroups(filters); }, [loadGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    Object.values(debounces.current).forEach(clearTimeout);
  }, []);

  const applyFilter = (key, value) => {
    const next = { ...filtersRef.current, [key]: value };
    setFilters(next);
    setExpandedKey(null);
    loadGroups(next);
  };

  const onFilterInput = (key, value) => {
    setInputs((p) => ({ ...p, [key]: value }));
    clearTimeout(debounces.current[key]);
    debounces.current[key] = setTimeout(() => applyFilter(key, value), 350);
  };

  const clearFilter = (key) => {
    setInputs((p) => ({ ...p, [key]: '' }));
    clearTimeout(debounces.current[key]);
    applyFilter(key, '');
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  // ── Group items ──
  const loadGroupItems = useCallback(async (g) => {
    setLoadingItems(true);
    try {
      const params = new URLSearchParams({ product_code: g.product_code });
      if (g.variant_code) params.append('variant_code', g.variant_code);
      if (g.lot_no) params.append('lot_no', g.lot_no);
      const r = await fastReactAPI.getRestockGroupItems(params.toString());
      if (r.data.success) {
        setItemsByGroup((prev) => ({ ...prev, [groupKey(g)]: r.data.items }));
      }
    } catch (e) {
      toast_error(e.response?.data?.error || 'Erro ao carregar linhas do grupo.');
    } finally {
      setLoadingItems(false);
    }
  }, [toast_error]);

  // Master/detail: tapping a group opens it on its own (the list is hidden);
  // "Voltar" returns to the list.
  const handleSelect = (g) => {
    setExpandedKey(groupKey(g));
    window.scrollTo(0, 0);
    loadGroupItems(g);   // always refetch: lines may have been handled by someone else
  };

  const selectedGroup = expandedKey ? groups.find((x) => groupKey(x) === expandedKey) : null;

  // the open group vanished (all its lines were completed) → back to the list
  useEffect(() => {
    if (expandedKey && !loading && !selectedGroup) setExpandedKey(null);
  }, [expandedKey, loading, selectedGroup]);

  // After an action the line leaves RESTOCK, so the group's aggregates (cones,
  // clusters, machines, oldest timer) change and an empty group disappears.
  // Reload from the server instead of patching state by hand.
  const refreshAfterAction = async (g) => {
    await loadGroups(filtersRef.current);
    if (g) await loadGroupItems(g);
  };

  const runConfirmed = async () => {
    if (!confirm || saving) return;
    const { type, group, item } = confirm;
    setSaving(true);
    try {
      if (type === 'advance') {
        const r = await fastReactAPI.advancePreparationStatus(item.preparation_id, null);
        if (r.data.success) toast_success('Reabastecimento concluído.');
      } else {
        const r = await fastReactAPI.revertPreparationStatus(item.preparation_id);
        if (r.data.success) toast_success('Estado revertido.');
      }
      setConfirm(null);
      await refreshAfterAction(group);
    } catch (e) {
      toast_error(e.response?.data?.error || (type === 'advance' ? 'Erro ao avançar estado.' : 'Erro ao reverter estado.'));
    } finally {
      setSaving(false);
    }
  };

  const refreshAll = async () => {
    await loadGroups(filtersRef.current);
    const g = groups.find((x) => groupKey(x) === expandedKey);
    if (g) loadGroupItems(g);
  };

  // ── Render ──
  return (
    <MobileLayout title="Reabastecer (Agrupado)">
      {selectedGroup ? (
        /* ── Detail: one group, its lines ── */
        (() => {
          const g = selectedGroup;
          const items = itemsByGroup[groupKey(g)] || [];
          const timer = formatRestockTimer(g.oldest_restock_request_at);
          return (
            <>
              <div className="mb-rg-toolbar">
                <button type="button" className="mb-rg-tool" onClick={() => setExpandedKey(null)}>
                  <FiArrowLeft size={15} /> Voltar
                </button>
                <button type="button" className="mb-rg-tool" onClick={refreshAll} disabled={loading || loadingItems}>
                  <FiRefreshCw size={15} className={loading || loadingItems ? 'mb-rg-spin' : ''} /> Atualizar
                </button>
              </div>

              <div className="mb-rg-card mb-rg-card--open mb-rg-summary">
                <div className="mb-rg-head mb-rg-head--static">
                  <span className="mb-rg-main">
                    <span className="mb-rg-code">
                      {g.product_code}{g.variant_code ? ` - ${g.variant_code}` : ''}
                    </span>
                    {g.lot_no && <span className="mb-rg-lot">Lote: {g.lot_no}</span>}
                    {g.product_description && <span className="mb-rg-desc">{g.product_description}</span>}
                    <span className="mb-rg-stats">
                      <span><strong>{g.total_cones ?? '—'}</strong> cones</span>
                      <span><strong>{g.cluster_count}</strong> núcleos</span>
                      <span><strong>{g.machine_count}</strong> máq.</span>
                    </span>
                  </span>
                  {timer && (
                    <span className={`mb-rg-timer ${timer.overdue ? 'mb-rg-timer--overdue' : ''}`}>
                      <FiClock size={12} /> {timer.text}
                    </span>
                  )}
                </div>
              </div>

              <div className="mb-rg-card">
                <div className="mb-rg-items mb-rg-items--plain">
                  {loadingItems && !items.length ? (
                    <div className="mb-rg-msg">A carregar…</div>
                  ) : !items.length ? (
                    <div className="mb-rg-msg">Sem linhas.</div>
                  ) : items.map((item) => (
                    <div key={item.preparation_id} className="mb-rg-item">
                      <div className="mb-rg-item-info">
                        <div className="mb-rg-item-po">{item.po_code_line_full}</div>
                        <div className="mb-rg-item-tags">
                          {item.machine_cluster && <span className="mb-rg-tag">Núcleo {item.machine_cluster}</span>}
                          {item.machine && <span className="mb-rg-tag mb-rg-tag--machine">Máq. {item.machine}</span>}
                          {item.location_position && <span className="mb-rg-tag mb-rg-tag--loc">📍 {item.location_position}</span>}
                          {item.container_material && <span className="mb-rg-tag">Carrinho {item.container_material}</span>}
                        </div>
                        <div className="mb-rg-item-meta">
                          <span>Qtd {fmtQty(item.quantity)}</span>
                          <span>Cones {item.num_restock_cones ?? '—'}</span>
                        </div>
                      </div>
                      <div className="mb-rg-item-actions">
                        <button type="button" className="mb-rg-act mb-rg-act--go"
                          onClick={() => setConfirm({ type: 'advance', group: g, item })}
                          title="Iniciar reabastecimento">
                          <FiPlay size={18} />
                        </button>
                        <button type="button" className="mb-rg-act mb-rg-act--undo"
                          onClick={() => setConfirm({ type: 'revert', group: g, item })}
                          title="Desfazer">
                          <FiRotateCcw size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          );
        })()
      ) : (
        /* ── List of groups ── */
        <>
          <div className="mb-rg-toolbar">
            <button type="button" className={`mb-rg-tool ${activeFilterCount ? 'mb-rg-tool--on' : ''}`}
              onClick={() => setShowFilters((s) => !s)}>
              <FiFilter size={15} /> Filtros{activeFilterCount ? ` (${activeFilterCount})` : ''}
            </button>
            <button type="button" className="mb-rg-tool" onClick={refreshAll} disabled={loading}>
              <FiRefreshCw size={15} className={loading ? 'mb-rg-spin' : ''} /> Atualizar
            </button>
          </div>

          {showFilters && (
            <div className="mb-scan-box">
              <div className="mb-rg-filter-grid">
                {FILTER_FIELDS.map(([key, label]) => (
                  <div key={key} className="mb-rg-filter">
                    <label>{label}</label>
                    <div className="mb-rg-filter-wrap">
                      <input
                        type="text"
                        autoComplete="off"
                        autoCapitalize="characters"
                        value={inputs[key]}
                        onChange={(e) => onFilterInput(key, e.target.value)}
                      />
                      {inputs[key] && (
                        <button type="button" className="mb-rg-filter-clear" onClick={() => clearFilter(key)} aria-label="Limpar">
                          <FiX size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {loading && !groups.length ? (
            <div className="mb-state"><div className="mb-spinner" /><span>A carregar…</span></div>
          ) : error ? (
            <div className="mb-state"><span style={{ color: 'var(--error)' }}>{error}</span></div>
          ) : !groups.length ? (
            <div className="mb-state"><span>Sem registos para reabastecer.</span></div>
          ) : (
            <div className="mb-rg-list">
              {groups.map((g) => {
                const timer = formatRestockTimer(g.oldest_restock_request_at);
                return (
                  <div key={groupKey(g)} className="mb-rg-card">
                    <button type="button" className="mb-rg-head" onClick={() => handleSelect(g)}>
                      <span className="mb-rg-main">
                        <span className="mb-rg-code">
                          {g.product_code}{g.variant_code ? ` - ${g.variant_code}` : ''}
                        </span>
                        {g.lot_no && <span className="mb-rg-lot">Lote: {g.lot_no}</span>}
                        {g.product_description && <span className="mb-rg-desc">{g.product_description}</span>}
                        <span className="mb-rg-stats">
                          <span><strong>{g.total_cones ?? '—'}</strong> cones</span>
                          <span><strong>{g.cluster_count}</strong> núcleos</span>
                          <span><strong>{g.machine_count}</strong> máq.</span>
                        </span>
                      </span>
                      {timer && (
                        <span className={`mb-rg-timer ${timer.overdue ? 'mb-rg-timer--overdue' : ''}`}>
                          <FiClock size={12} /> {timer.text}
                        </span>
                      )}
                      <span className="mb-rg-chev"><FiChevronRight size={18} /></span>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* confirmation */}
      {confirm && (
        <div className="mb-rg-overlay" onClick={() => { if (!saving) setConfirm(null); }}>
          <div className="mb-rg-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="mb-rg-dialog-title">
              {confirm.type === 'advance' ? '▶️ Concluir reabastecimento' : '↩️ Desfazer'}
            </div>
            <div className="mb-rg-dialog-code">
              {confirm.item.po_code_line_full} · {confirm.group.product_code}
              {confirm.group.variant_code ? ` - ${confirm.group.variant_code}` : ''}
            </div>
            <div className="mb-rg-dialog-text">
              {confirm.type === 'advance'
                ? 'A linha é marcada como reabastecida (Recolhido).'
                : 'Reverte o estado desta linha.'}
            </div>
            <div className="mb-rg-dialog-btns">
              <button type="button" disabled={saving} onClick={() => setConfirm(null)}
                className="mb-rg-dialog-btn">
                Cancelar
              </button>
              <button type="button" disabled={saving} onClick={runConfirmed}
                className={`mb-rg-dialog-btn mb-rg-dialog-btn--${confirm.type === 'advance' ? 'ok' : 'danger'}`}>
                {saving ? 'A processar…' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="toast-container">
        {toasts.map((toast) => (
          <Toast key={toast.id} id={toast.id} message={toast.message}
            type={toast.type} duration={toast.duration}
            requireConfirm={toast.requireConfirm} onConfirm={toast.onConfirm}
            onClose={removeToast} />
        ))}
      </div>
    </MobileLayout>
  );
};

export default MobileRestockGroups;